import { MutableRefObject } from "react";
import { PathInfo, SimpleOption, SimpleOption2 } from "../AmbientFacilitiesContext";
import { Parsed, ParsingRequest, PathQuery, PathPart, ParsingRequestContext } from "./types";

export const parsePathQuery = (
  s: string, 
  pathInfoRef: MutableRefObject<PathInfo>,
  setPathInfo:(x: PathInfo) => void,
  pushParsedResult:(x: Parsed) => void,
  parsingRequests: MutableRefObject<ParsingRequest[]>,
) => {
  const trimedS = (s || '').replace(/^\s*\/\s*?/i, '').trim();
  let error: string|undefined = undefined
  let position: [number, number]|undefined = undefined
  let arr = trimedS.split('.')

  if (arr.length > 4) {
    error = 'Path search can not contain more than 4 parts'
    position = [trimedS.indexOf(arr[3]) + arr[3].length, trimedS.length - 1]
    pushParsedResult({
      raw: s,
      ast: {
        kind: 'Path',
        value: trimedS,
        prefix: '/ ',
        progress: -1,
      },
      guess: [],
      error,
      position,
    })
    return;
  }

  let baseObj: Parsed = { raw: s, guess: [] } as any
  let pathQuery: PathQuery = { kind: 'Path', value: s, prefix: '/ ', progress: arr.length - 1 }
  const trimedArr = arr.map(x => x.trim())

  const wrapFuzzyValue = (value: string) => {
    const str = value.replace(/^\*+/, '').replace(/\*+$/, '')
    return str.length === 0 ? '*' : `*${str}*`
  }
  const buildPartFromDatasetMatch = (n:number, dataset:Array<SimpleOption|SimpleOption2>) => {
    const part = trimedArr[n]
    const item = dataset.find(d => d.text.toLocaleLowerCase() === part.toLocaleLowerCase())
    const isFuzzy = !item || item.text === '*'
    const t:PathPart = {
      value: isFuzzy ?  wrapFuzzyValue(part) : (item?.text || part),
      raw: part,
      usage: isFuzzy ? 'fuzzy' : 'exact',
      id: isFuzzy ? undefined : item?.key,
    }
    return t
  }
  const buildPartWithoutMatch = (n:number) => {
    const part = trimedArr[n]
    const isFuzzy = part.indexOf('*') > -1 || part === ''
    const t:PathPart = {
      value: isFuzzy ? wrapFuzzyValue(part) : part,
      raw: part,
      usage: isFuzzy ? 'fuzzy' : 'exact',
      id: undefined,
    }
    return t
  }

  const pushPartialResult = () => {
    pushParsedResult({ ...baseObj, ast: pathQuery, error: 'parsing express..', position: [s.length, s.length] })
  }

  const process = ({
    databaseOptions,
    schemaOptions,
    tableOptions,
    fieldOptions,
  }: ParsingRequestContext) : ParsingRequest => {

    if (0 <= pathQuery.progress && !pathQuery.database) {
      const t = buildPartFromDatasetMatch(0, databaseOptions)

      pathQuery = { ...pathQuery, database: t }
      
      // check pathInfo to decide whether to fetch new data
      if (pathInfoRef.current.database !== pathQuery.database?.id) {
        setPathInfo({ database: pathQuery.database?.id as number|undefined, })
        
        if (pathQuery.progress !== 0) {
          return unfinishedRequest; // stop here and wait for another round
        }
      }
    }
    
    if (1 <= pathQuery.progress && !pathQuery.schema) {
      if (pathQuery.database?.usage === 'fuzzy') {
        // database is indeterminate, then schema can't be accurate,
        // don't bother try to find a match
        const t = buildPartWithoutMatch(1)

        pathQuery = { ...pathQuery, schema: t }

      } else if (pathQuery.database?.id !== pathInfoRef.current.database) {
        setPathInfo({ database: pathQuery.database?.id as number|undefined })
        pushPartialResult()
        return unfinishedRequest; // stop here and wait for another round
        
      } else if (schemaOptions.length === 0) {
        return unfinishedRequest; // stop and wait for data refresh
        
      } else {
        const t = buildPartFromDatasetMatch(1, schemaOptions)

        pathQuery = { ...pathQuery, schema: t }

        if (pathInfoRef.current.schema !== pathQuery.schema?.id) {
          setPathInfo({ ...pathInfoRef.current, schema: pathQuery.schema?.id as string|undefined })
          
          if (pathQuery.progress !== 1) {
            return unfinishedRequest; // stop here and wait for another round
          }
        }
      }
    }
    
    if (2 <= pathQuery.progress && !pathQuery.table) {
      if (pathQuery.database?.usage === 'fuzzy' || pathQuery.schema?.usage === 'fuzzy') {
        const t = buildPartWithoutMatch(2)
        pathQuery = { ...pathQuery, table: t }

      } else if (pathQuery.schema?.id !== pathInfoRef.current.schema) {
        setPathInfo({ 
          database: pathQuery.database?.id as number|undefined,
          schema: pathQuery.schema?.id as string|undefined,
        })
        pushPartialResult()
        return unfinishedRequest; // stop here and wait for another round
        
      } else if (tableOptions.length === 0) {
        return unfinishedRequest; // stop and wait for data refresh
          
      } else {
        const t = buildPartFromDatasetMatch(2, tableOptions)

        pathQuery = { ...pathQuery, table: t }

        if (pathInfoRef.current.table !== pathQuery.table?.id) {
          setPathInfo({ ...pathInfoRef.current, table: pathQuery.table?.id as string|undefined })
          
          if (pathQuery.progress !== 2) {
            return unfinishedRequest; // stop here and wait for another round
          }
        }
      }
    }
    
    if (3 <= pathQuery.progress && !pathQuery.field) {
      if (pathQuery.database?.usage === 'fuzzy' 
      || pathQuery.schema?.usage === 'fuzzy'
      || pathQuery.table?.usage === 'fuzzy'
      ) {
        const t = buildPartWithoutMatch(3)
        pathQuery = { ...pathQuery, field: t }

      } else if (pathQuery.table?.id !== pathInfoRef.current.table) {
        setPathInfo({ 
          database: pathQuery.database?.id as number|undefined,
          schema: pathQuery.schema?.id as string|undefined,
          table: pathQuery.table?.id as string|undefined,
        })
        pushPartialResult()
        return unfinishedRequest; // stop here and wait for another round
        
      } else if (fieldOptions.length === 0) {
        return unfinishedRequest; // stop and wait for data refresh
        
      } else {
        const t = buildPartFromDatasetMatch(3, fieldOptions)

        pathQuery = { ...pathQuery, field: t }
      }
    }

    const valueObj = (() => {
      const parts = [pathQuery.database, pathQuery.schema, pathQuery.table, pathQuery.field]
      const left = [...parts].splice(0, pathQuery.progress) as PathPart[]
      const current = parts[pathQuery.progress] as PathPart
      const values = [...left.map(x => x.value || ''), current.value || '']
      const value = !!(current.raw) ? values.join('.') : values.join('.').replace(/\.\**$/, '.')
      
      let amendment = undefined
      if (left.some(p => p?.raw !== p?.value) || /^\s*\/\S/.test(baseObj.raw)) {
        amendment = [...left.map(x => x.value || ''), current.raw].join('.')
      }
      return {value, amendment}
    })()
    pathQuery = { ...pathQuery, ...valueObj}
    
    const finishedReq: ParsingRequest = {
      value: s,
      process: () => finishedReq,
      finished: true,
      result: { ...baseObj, ast: pathQuery, error, position }
    }

    return finishedReq
  }
  
  const unfinishedRequest = {
    value: s,
    process,
    finished: false
  }

  parsingRequests.current.push(unfinishedRequest)
}

// 
// Path Expression Auto Amendment:
//   when parser parsed user input expression string into [database, schema, table, field] 4 parts,
//   the interpreted parts might be different with original input (, like: we think one part of input
//   shall be used by a fuzzy match instead of a exact match, thus we wrap the part with "*"s); 
//   at some points we should make user aware of the interpretation change.
// 
//   These points of time would include:
//   1) when user type a "." character to explicitly end the current piece of input
//   2) when user select an option from autocomplete suggestions thus the current piece of input ended,
//      and a "." character is auto appended
//   3) when user type an "Enter" key to submit overall expression input
// 
//   [interpreted part, interpreted part, ... current part]
//   when Auto Amendment action happens, "current part" should be keeped, we replace other parts
//   with what the parser interpreted; unless in #3 case, in that case we should replace the whole expression
// 

